import { useState, useEffect, useCallback, useMemo } from 'react'
import axios from 'lib/axios'
import { SSMHistoryOverview, ExpenseTrendDataPoint } from 'models'

const transformExpenseTrendData = (data: SSMHistoryOverview): ExpenseTrendDataPoint[] => {
  const currentYear = new Date().getFullYear()
  const currentQuarter = Math.ceil((new Date().getMonth() + 1) / 3)
  const points: ExpenseTrendDataPoint[] = []

  data.years
    .slice()
    .sort((a, b) => a.year - b.year)
    .forEach((yearData) => {
      yearData.quarters_expense.forEach((quarterData: any, qIndex: number) => {
        const quarter = qIndex + 1
        const isEstimated = yearData.year > currentYear || (yearData.year === currentYear && quarter > currentQuarter)

        // Support both new object format and legacy number format
        const isObject = typeof quarterData === 'object' && quarterData !== null
        points.push({
          year: yearData.year,
          quarter,
          label: `Q${quarter} ${yearData.year}`,
          expense: isObject ? quarterData.total : quarterData,
          isEstimated,
          nistBreakdown: isObject ? quarterData.nist_pillars_data || [] : yearData.nist_pillars_data || [],
          assetTypesBreakdown: isObject ? quarterData.asset_types_data || [] : yearData.asset_types_data || []
        })
      })
    })

  return points
}

export const useSSMHistoryData = () => {
  const [data, setData] = useState<SSMHistoryOverview | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchHistoryData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await axios.get<SSMHistoryOverview>('/api/v3/ssm/history')
      if (res.status === 200) {
        setData(res.data)
      }
    } catch (e) {
      console.error(e)
      setError('Failed to load historical data')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchHistoryData()
  }, [fetchHistoryData])

  const expenseTrendData = useMemo(() => (data ? transformExpenseTrendData(data) : []), [data])

  const sortedYears = useMemo(() => (data ? data.years.slice().sort((a, b) => a.year - b.year) : []), [data])

  return {
    data,
    loading,
    error,
    expenseTrendData,
    sortedYears,
    nistPillars: data?.nist_pillars || [],
    assetTypes: data?.asset_types || [],
    refetch: fetchHistoryData
  }
}
